import { useCallback, useEffect, useState } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Patient, Condition } from '@medplum/fhirtypes';
import { HL7, SYSTEMS } from '@/consts/systems.consts';

export interface ConditionsData {
  readonly problems: Condition[];
  readonly geneticDiseases: Condition[];
  readonly hpoConditions: Condition[];
  readonly medicalHistory: Condition[];
  readonly encounterDiagnoses: Condition[];
  readonly all: Condition[];
}

const { CODES, BASE } = HL7.TERMINOLOGY.CS.CONDITION.CATEGORY;

// Helper function to categorize conditions
const hasCategory = (condition: Condition, categoryCode: string): boolean => {
  return (
    condition.category?.some((category) =>
      category.coding?.some((coding) => {
        const isValidSystem = coding.system === BASE || !coding.system;
        return isValidSystem && coding.code === categoryCode;
      })
    ) ?? false
  );
};

// Hook to fetch and subscribe to patient conditions with real-time updates.
// This follows the same pattern as usePatientSummaryData from the working branch.
export const useConditionsData = (patient?: Patient, enabled = true) => {
  const medplum = useMedplum();
  const [data, setData] = useState<ConditionsData>({
    problems: [],
    geneticDiseases: [],
    hpoConditions: [],
    medicalHistory: [],
    encounterDiagnoses: [],
    all: [],
  });
  const [loading, setLoading] = useState(false);

  const fetchConditions = useCallback(async () => {
    if (!patient?.id) {
      // Clear data if no patient
      setData({
        problems: [],
        geneticDiseases: [],
        hpoConditions: [],
        medicalHistory: [],
        encounterDiagnoses: [],
        all: [],
      });
      return;
    }

    // Invalidate cache to ensure fresh data
    medplum.invalidateSearches('Condition');

    setLoading(true);
    try {
      const ref = `Patient/${patient.id}`;
      const searchMeta = { _count: 100, _sort: '-_lastUpdated' };

      const allConditions = await medplum.searchResources('Condition', {
        patient: ref,
        ...searchMeta,
      });

      // Filter conditions by category
      const problems: Condition[] = [];
      const geneticDiseases: Condition[] = [];
      const hpoConditions: Condition[] = [];
      const medicalHistory: Condition[] = [];
      const encounterDiagnoses: Condition[] = [];

      for (const condition of allConditions) {
        // Check if it's an HPO condition (by code system)
        const isHpo = condition.code?.coding?.some(
          (coding) => coding.system === SYSTEMS.HPO
        );
        if (isHpo) {
          hpoConditions.push(condition);
        }

        // Check categories
        if (hasCategory(condition, CODES.PROBLEM_LIST_ITEM)) {
          problems.push(condition);
        }
        if (hasCategory(condition, CODES.GENETIC_DISEASE)) {
          geneticDiseases.push(condition);
        }
        if (hasCategory(condition, CODES.MEDICAL_HISTORY)) {
          medicalHistory.push(condition);
        }
        if (hasCategory(condition, CODES.ENCOUNTER_DIAGNOSIS)) {
          encounterDiagnoses.push(condition);
        }
      }

      setData({
        problems,
        geneticDiseases,
        hpoConditions,
        medicalHistory,
        encounterDiagnoses,
        all: allConditions,
      });
    } catch (error) {
      console.error('Error loading conditions:', error);
    } finally {
      setLoading(false);
    }
  }, [patient?.id, medplum]);

  // Load conditions on mount and when patient changes
  useEffect(() => {
    if (!enabled) {
      // Clear data when disabled
      setData({
        problems: [],
        geneticDiseases: [],
        hpoConditions: [],
        medicalHistory: [],
        encounterDiagnoses: [],
        all: [],
      });
      return;
    }
    fetchConditions();
  }, [fetchConditions, enabled]);

  // Subscribe to condition changes for real-time updates
  useSubscription(
    enabled && patient?.id ? `Condition?patient=Patient/${patient.id}` : '',
    () => {
      fetchConditions();
    }
  );

  return { data, loading, refetch: fetchConditions };
};
